import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  FolderOpen, Upload, Download, Trash2, File, FileText, Image, PenLine,
  AlertTriangle, Clock, Shield, CheckCircle2, Lock, Send, RefreshCw, X,
  BookOpen, ChevronDown, ChevronUp, Settings2, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FOLDERS = ['general', 'training', 'hr', 'compliance', 'personal', 'timesheets'];

function WarningBanner({ lockoutStatus }) {
  if (!lockoutStatus || lockoutStatus.highest_level === 'none') return null;
  const isLockout = lockoutStatus.highest_level === 'lockout';
  const isFinal = lockoutStatus.highest_level === 'final_warning';

  return (
    <div className={`w-full p-4 rounded-xl border flex items-start gap-3 ${
      isLockout ? 'bg-red-500/10 border-red-500/40' :
      isFinal ? 'bg-orange-500/10 border-orange-500/40' :
      'bg-amber-500/10 border-amber-500/40'
    }`} data-testid="compliance-warning-banner">
      {isLockout ? <Lock className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" /> :
       isFinal ? <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" /> :
       <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />}
      <div className="flex-1">
        <p className={`text-sm font-semibold ${isLockout ? 'text-red-400' : isFinal ? 'text-orange-400' : 'text-amber-400'}`}>
          {isLockout ? 'Dashboard Access Restricted' : isFinal ? 'Final 24-Hour Warning' : 'Training Documents Pending'}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {isLockout
            ? 'You have overdue training documents. Complete sign-offs below to restore full access.'
            : isFinal
            ? 'You have less than 24 hours to complete required sign-offs before dashboard access is restricted.'
            : `${lockoutStatus.overdue_documents.length} document(s) require your sign-off within the grace period.`}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {lockoutStatus.overdue_documents.map(d => (
            <span key={d.document_id} className={`text-[10px] px-2 py-0.5 rounded-full ${
              d.warning_level === 'lockout' ? 'bg-red-500/20 text-red-400' :
              d.warning_level === 'final_warning' ? 'bg-orange-500/20 text-orange-400' :
              'bg-amber-500/20 text-amber-400'
            }`}>
              {d.filename} ({d.days_remaining}d left)
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrainingSignOff({ doc, onComplete }) {
  const [step, setStep] = useState(doc.acknowledged ? 'done' : 'read');
  const [viewing, setViewing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);

  const confirmRead = async () => {
    try {
      await axios.post(`${API_URL}/api/docmgmt/documents/${doc.id}/acknowledge`, {}, { withCredentials: true });
      setStep('sign');
      toast.success('Read confirmation recorded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const submitSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error('Please draw your signature');
    setSubmitting(true);
    try {
      const dataUrl = sigRef.current.toDataURL('image/png');
      const resp = await fetch(dataUrl);
      const blob = await resp.blob();
      const formData = new FormData();
      formData.append('signature', blob, 'signature.png');
      await axios.post(`${API_URL}/api/docmgmt/documents/${doc.id}/sign`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document signed off successfully');
      setStep('done');
      if (onComplete) onComplete();
    } catch (err) { toast.error(err.response?.data?.detail || 'Signature failed'); }
    setSubmitting(false);
  };

  const levelColor = doc.warning_level === 'lockout' ? 'border-red-500/40' :
    doc.warning_level === 'final_warning' ? 'border-orange-500/40' :
    doc.warning_level === 'pending' ? 'border-amber-500/40' : 'border-[#A8E6CF]/40';
  const levelBg = doc.warning_level === 'lockout' ? 'bg-red-500/5' :
    doc.warning_level === 'final_warning' ? 'bg-orange-500/5' :
    doc.warning_level === 'pending' ? 'bg-amber-500/5' : 'bg-[#A8E6CF]/5';

  return (
    <Card className={`glass-card ${levelColor} ${levelBg}`} data-testid={`training-doc-${doc.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#FF6F61] flex-shrink-0" />
              <h3 className="text-sm text-foreground font-medium truncate">{doc.filename}</h3>
              {step === 'done' && <CheckCircle2 className="w-4 h-4 text-[#A8E6CF]" />}
            </div>
            {doc.description && <p className="text-xs text-muted-foreground mt-1">{doc.description}</p>}
            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
              <span>Grace: {doc.grace_period_days}d</span>
              {doc.warning_level !== 'completed' && <span className={`px-1.5 py-0.5 rounded-full ${
                doc.warning_level === 'lockout' ? 'bg-red-500/10 text-red-400' :
                doc.warning_level === 'final_warning' ? 'bg-orange-500/10 text-orange-400' :
                'bg-amber-500/10 text-amber-400'
              }`}>{doc.days_remaining}d remaining</span>}
            </div>
          </div>
          <Button size="sm" variant="outline" className="text-xs border-border"
            onClick={() => window.open(`${API_URL}/api/docmgmt/documents/${doc.id}/download`, '_blank')}
            data-testid={`view-training-${doc.id}`}>
            <BookOpen className="w-3 h-3 mr-1" />View
          </Button>
        </div>

        {step === 'done' ? (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-[#A8E6CF]/10 text-xs text-[#A8E6CF]">
            <CheckCircle2 className="w-3.5 h-3.5" />Signed off
          </div>
        ) : step === 'read' ? (
          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm" onClick={confirmRead} data-testid={`read-confirm-${doc.id}`}>
            <BookOpen className="w-4 h-4 mr-2" />I have Read & Understood this Document
          </Button>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Step 2: Draw your digital signature below</p>
            <div className="border border-border rounded-lg bg-white overflow-hidden" data-testid={`sig-canvas-${doc.id}`}>
              <SignatureCanvas
                ref={sigRef}
                penColor="#1a1a2e"
                canvasProps={{ width: 500, height: 150, className: 'w-full h-[150px]' }}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-xs border-border text-muted-foreground" onClick={() => sigRef.current?.clear()}>Clear</Button>
              <Button size="sm" className="flex-1 btn-primary text-xs" onClick={submitSignature} disabled={submitting} data-testid={`submit-sig-${doc.id}`}>
                {submitting ? 'Submitting...' : 'Submit Signature & Complete Sign-Off'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function DocumentStorage() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [trainingDocs, setTrainingDocs] = useState([]);
  const [lockoutStatus, setLockoutStatus] = useState(null);
  const [complianceOverview, setComplianceOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [folderType, setFolderType] = useState('general');
  const [requiresSignoff, setRequiresSignoff] = useState(false);
  const [gracePeriod, setGracePeriod] = useState(7);
  const [targetRoles, setTargetRoles] = useState('caregiver');
  const [uploading, setUploading] = useState(false);
  const [filterFolder, setFilterFolder] = useState('all');
  const [tab, setTab] = useState('files');
  const [seeding, setSeeding] = useState(false);
  const [versionDialog, setVersionDialog] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionFile, setVersionFile] = useState(null);
  const [versionDesc, setVersionDesc] = useState('');
  const [versionGrace, setVersionGrace] = useState(7);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [myCompDocs, setMyCompDocs] = useState([]);
  const [compUploadOpen, setCompUploadOpen] = useState(false);
  const [compFile, setCompFile] = useState(null);
  const [compDocType, setCompDocType] = useState('DBS Check');
  const [compTitle, setCompTitle] = useState('');
  const [compExpiry, setCompExpiry] = useState('');
  const [compNotes, setCompNotes] = useState('');
  const [compUploading, setCompUploading] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkDocId, setBulkDocId] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  const isAdmin = user?.role === 'agency_admin' || user?.role === 'ceo' || user?.role === 'developer';
  const isCaregiver = user?.role === 'caregiver';

  const COMP_DOC_TYPES = ['DBS Check', 'Right to Work', 'Qualifications', 'Training Certificate', 'Insurance', 'Driving License', 'Reference', 'Health Declaration', 'First Aid Certificate', 'Safeguarding Certificate'];

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, trainingRes, lockoutRes] = await Promise.all([
        axios.get(`${API_URL}/api/docmgmt/documents`, { withCredentials: true }),
        axios.get(`${API_URL}/api/docmgmt/training-documents`, { withCredentials: true }),
        axios.get(`${API_URL}/api/docmgmt/lockout-status`, { withCredentials: true }),
      ]);
      setDocuments(docsRes.data);
      setTrainingDocs(trainingRes.data);
      setLockoutStatus(lockoutRes.data);

      if (isAdmin) {
        const [compRes, reviewsRes] = await Promise.all([
          axios.get(`${API_URL}/api/docmgmt/admin/compliance-overview`, { withCredentials: true }),
          axios.get(`${API_URL}/api/docmgmt/admin/pending-reviews`, { withCredentials: true }),
        ]);
        setComplianceOverview(compRes.data);
        setPendingReviews(reviewsRes.data);
      }
      // Fetch caregiver's own compliance docs
      if (isCaregiver || isAdmin) {
        const myRes = await axios.get(`${API_URL}/api/docmgmt/caregiver/my-compliance-docs`, { withCredentials: true });
        setMyCompDocs(myRes.data);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [isAdmin, isCaregiver]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpload = async () => {
    if (!file) return toast.error('Select a file');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder_type', folderType);
      formData.append('description', description);
      formData.append('requires_signoff', requiresSignoff);
      formData.append('grace_period_days', gracePeriod);
      formData.append('target_roles', targetRoles);
      await axios.post(`${API_URL}/api/docmgmt/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setUploadDialog(false);
      setFile(null);
      setDescription('');
      setRequiresSignoff(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/docmgmt/documents/${docId}`, { withCredentials: true });
      toast.success('Document deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleSeedSamples = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/docmgmt/admin/seed-training-samples`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Seeding failed'); }
    setSeeding(false);
  };

  const sendLockoutEmail = async (caregiverId) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/docmgmt/admin/send-lockout-email/${caregiverId}`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch { toast.error('Failed to send email'); }
  };

  const downloadComplianceReport = () => {
    window.open(`${API_URL}/api/docmgmt/admin/compliance-report`, '_blank');
  };

  const openVersionDialog = async (docId) => {
    setVersionDialog(docId);
    setVersionFile(null);
    setVersionDesc('');
    setVersionGrace(7);
    try {
      const { data } = await axios.get(`${API_URL}/api/docmgmt/documents/${docId}/versions`, { withCredentials: true });
      setVersions(data);
    } catch { setVersions([]); }
  };

  const uploadNewVersion = async () => {
    if (!versionFile || !versionDialog) return toast.error('Select a file');
    setUploadingVersion(true);
    try {
      const formData = new FormData();
      formData.append('file', versionFile);
      formData.append('description', versionDesc);
      formData.append('grace_period_days', versionGrace);
      await axios.post(`${API_URL}/api/docmgmt/documents/${versionDialog}/new-version`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('New version uploaded');
      setVersionDialog(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Version upload failed'); }
    setUploadingVersion(false);
  };

  const handleComplianceUpload = async () => {
    if (!compFile) return toast.error('Select a file');
    setCompUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', compFile);
      formData.append('document_type', compDocType);
      formData.append('title', compTitle || `${compDocType} - ${compFile.name}`);
      formData.append('expiry_date', compExpiry);
      formData.append('notes', compNotes);
      await axios.post(`${API_URL}/api/docmgmt/caregiver/upload-compliance`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Compliance document uploaded');
      setCompUploadOpen(false);
      setCompFile(null);
      setCompTitle('');
      setCompExpiry('');
      setCompNotes('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setCompUploading(false);
  };

  const downloadAuditCSV = () => {
    window.open(`${API_URL}/api/docmgmt/admin/audit-trail-csv`, '_blank');
  };

  const handleBulkAssign = async () => {
    if (!bulkDocId) return toast.error('Select a training document');
    setBulkAssigning(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/docmgmt/admin/bulk-assign`, { document_id: bulkDocId }, { withCredentials: true });
      toast.success(`${data.assigned} caregiver(s) assigned (${data.already_assigned} already had it)`);
      setBulkAssignOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk assign failed'); }
    setBulkAssigning(false);
  };

  const handleReviewDoc = async (docId, decision) => {
    try {
      await axios.put(`${API_URL}/api/docmgmt/admin/review-compliance-doc/${docId}`, { decision, notes: reviewNotes }, { withCredentials: true });
      toast.success(`Document ${decision}`);
      setReviewNotes('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Review failed'); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (ct) => {
    if (ct?.startsWith('image/')) return <Image className="w-5 h-5 text-blue-400" />;
    if (ct?.includes('pdf')) return <FileText className="w-5 h-5 text-red-400" />;
    return <File className="w-5 h-5 text-muted-foreground" />;
  };

  const filtered = filterFolder === 'all' ? documents : documents.filter(d => d.folder_type === filterFolder);
  const pendingTraining = trainingDocs.filter(d => !d.acknowledged);
  const nonCompliant = complianceOverview?.caregivers?.filter(c => c.action_required) || [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="document-storage-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <FolderOpen className="w-6 h-6 text-amber-400" />Document Management
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Upload, manage & sign-off documents</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" className="border-border text-muted-foreground" onClick={downloadComplianceReport} data-testid="compliance-report-btn">
                  <Download className="w-4 h-4 mr-1" />Report
                </Button>
                <Button variant="outline" size="sm" className="border-border text-muted-foreground" onClick={downloadAuditCSV} data-testid="audit-csv-btn">
                  <Download className="w-4 h-4 mr-1" />Audit CSV
                </Button>
                <Button variant="outline" size="sm" className="border-border text-muted-foreground" onClick={handleSeedSamples} disabled={seeding} data-testid="seed-training-btn">
                  {seeding ? 'Seeding...' : 'Seed Samples'}
                </Button>
              </>
            )}
            <Button className="btn-primary" onClick={() => setUploadDialog(true)} data-testid="upload-doc-btn"><Upload className="w-4 h-4 mr-2" />Upload</Button>
          </div>
        </div>

        {/* Warning Banner */}
        <WarningBanner lockoutStatus={lockoutStatus} />

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          <button onClick={() => setTab('files')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'files' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-files">
            All Files
          </button>
          <button onClick={() => setTab('training')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors relative ${tab === 'training' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-training">
            Training Sign-Offs
            {pendingTraining.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{pendingTraining.length}</span>
            )}
          </button>
          {isAdmin && (
            <button onClick={() => setTab('compliance')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors relative ${tab === 'compliance' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-compliance">
              Compliance
              {nonCompliant.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{nonCompliant.length}</span>
              )}
            </button>
          )}
          {isCaregiver && (
            <button onClick={() => setTab('mydocs')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'mydocs' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-mydocs">
              My Docs
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
        ) : tab === 'files' ? (
          <>
            {/* Folder filters */}
            <div className="flex gap-1.5 flex-wrap">
              <Button size="sm" variant={filterFolder === 'all' ? 'default' : 'outline'}
                className={filterFolder === 'all' ? 'bg-[#00A6A0] text-[#0F1419]' : 'border-border text-muted-foreground'}
                onClick={() => setFilterFolder('all')}>All</Button>
              {FOLDERS.map(f => (
                <Button key={f} size="sm" variant={filterFolder === f ? 'default' : 'outline'}
                  className={filterFolder === f ? 'bg-[#00A6A0] text-[#0F1419]' : 'border-border text-muted-foreground'}
                  onClick={() => setFilterFolder(f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</Button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-16"><FolderOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" /><p className="text-muted-foreground">No documents in this folder</p></div>
            ) : (
              <div className="space-y-2">
                {filtered.map(doc => (
                  <Card key={doc.id} className="glass-card border-border" data-testid={`doc-${doc.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      {getFileIcon(doc.content_type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground font-medium truncate">{doc.filename}</p>
                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                          <span>{formatSize(doc.file_size)}</span>
                          <span className="capitalize px-1.5 py-0.5 rounded-full bg-muted">{doc.folder_type}</span>
                          <span>by {doc.uploaded_by_name}</span>
                          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                          {doc.requires_signoff && <span className="px-1.5 py-0.5 rounded-full bg-[#FF6F61]/10 text-[#FF6F61]">Sign-off required</span>}
                          {doc.version && doc.version > 1 && <span className="px-1.5 py-0.5 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6]">v{doc.version}</span>}
                        </div>
                        {doc.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{doc.description}</p>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[#00A6A0]"
                          onClick={() => window.open(`${API_URL}/api/docmgmt/documents/${doc.id}/download`, '_blank')} data-testid={`download-doc-${doc.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        {isAdmin && doc.requires_signoff && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[#8B5CF6]" title="Version history"
                            onClick={() => openVersionDialog(doc.id)} data-testid={`version-doc-${doc.id}`}>
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400"
                            onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : tab === 'training' ? (
          <div className="space-y-4" data-testid="training-section">
            <p className="text-sm text-muted-foreground">Documents requiring your acknowledgement and digital signature. Read each document carefully, then complete the two-step sign-off process.</p>
            {trainingDocs.length === 0 ? (
              <div className="text-center py-12"><Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No training documents assigned</p></div>
            ) : (
              trainingDocs.map(doc => <TrainingSignOff key={doc.id} doc={doc} onComplete={fetchAll} />)
            )}
          </div>
        ) : tab === 'compliance' && isAdmin ? (
          <div className="space-y-4" data-testid="compliance-section">
            <div className="grid grid-cols-3 gap-3">
              <Card className="glass-card border-border">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{complianceOverview?.total_training_docs || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Training Docs</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-border">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-[#A8E6CF]">{complianceOverview?.new_signoffs_24h || 0}</p>
                  <p className="text-[10px] text-muted-foreground">New Sign-Offs (24h)</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-border">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-red-400">{nonCompliant.length}</p>
                  <p className="text-[10px] text-muted-foreground">Non-Compliant</p>
                </CardContent>
              </Card>
            </div>

            <div className="flex items-center gap-2 p-3 rounded-lg bg-[#8B5CF6]/5 border border-[#8B5CF6]/20" data-testid="auto-lockout-info">
              <Send className="w-4 h-4 text-[#8B5CF6] flex-shrink-0" />
              <p className="text-xs text-muted-foreground">
                <span className="text-[#8B5CF6] font-medium">Auto-lockout emails active:</span> Caregivers who exceed the grace period will automatically receive a restriction email daily at 08:00 UTC until they complete sign-offs.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-foreground">Caregiver Compliance Status</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="border-[#00A6A0]/30 text-[#00A6A0] text-xs" onClick={() => setBulkAssignOpen(true)} data-testid="bulk-assign-btn">
                  <Plus className="w-3 h-3 mr-1" />Bulk Assign
                </Button>
                <Button variant="outline" size="sm" className="border-border text-muted-foreground text-xs" onClick={downloadComplianceReport} data-testid="compliance-report-inline-btn">
                  <Download className="w-3 h-3 mr-1" />PDF Report
                </Button>
              </div>
            </div>

            {/* Pending Reviews */}
            {pendingReviews.length > 0 && (
              <div data-testid="pending-reviews-section">
                <h4 className="text-xs font-medium text-amber-400 mb-2">Pending Reviews ({pendingReviews.length})</h4>
                <div className="space-y-2">
                  {pendingReviews.map(doc => (
                    <Card key={doc.id} className="glass-card border-amber-500/30" data-testid={`review-${doc.id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground font-medium truncate">{doc.title || doc.filename}</p>
                            <p className="text-[10px] text-muted-foreground">{doc.caregiver_name} &bull; {doc.document_type} &bull; {new Date(doc.created_at).toLocaleDateString()}</p>
                          </div>
                          {doc.storage_path && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground"
                              onClick={() => window.open(`${API_URL}/api/docmgmt/caregiver/compliance-docs/${doc.id}/download`, '_blank')}>
                              <Download className="w-3 h-3 mr-1" />View
                            </Button>
                          )}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <Input value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                            placeholder="Review notes (optional)" className="bg-muted border-border text-foreground text-xs h-8 flex-1" />
                          <Button size="sm" className="h-8 bg-[#A8E6CF] text-[#0F1419] hover:bg-[#A8E6CF]/80 text-xs"
                            onClick={() => handleReviewDoc(doc.id, 'approved')} data-testid={`approve-${doc.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 border-red-500/30 text-red-400 text-xs"
                            onClick={() => handleReviewDoc(doc.id, 'rejected')} data-testid={`reject-${doc.id}`}>
                            <X className="w-3 h-3 mr-1" />Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            {(complianceOverview?.caregivers || []).map(cg => (
              <Card key={cg.caregiver_id} className={`glass-card border-border ${cg.lockout ? 'border-red-500/30' : cg.action_required ? 'border-amber-500/30' : 'border-[#A8E6CF]/30'}`}
                data-testid={`compliance-cg-${cg.caregiver_id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    cg.lockout ? 'bg-red-500/10' : cg.action_required ? 'bg-amber-500/10' : 'bg-[#A8E6CF]/10'
                  }`}>
                    {cg.lockout ? <Lock className="w-5 h-5 text-red-400" /> :
                     cg.action_required ? <AlertTriangle className="w-5 h-5 text-amber-400" /> :
                     <CheckCircle2 className="w-5 h-5 text-[#A8E6CF]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-foreground font-medium">{cg.name}</p>
                      {cg.action_required && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 font-medium" data-testid={`action-tag-${cg.caregiver_id}`}>Action Required</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{cg.completed}/{cg.total_required} completed</p>
                    {cg.pending_documents.length > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">Pending: {cg.pending_documents.join(', ')}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {cg.lockout && (
                      <Button size="sm" variant="outline" className="text-xs border-red-500/30 text-red-400"
                        onClick={() => sendLockoutEmail(cg.caregiver_id)} data-testid={`lockout-email-${cg.caregiver_id}`}>
                        <Send className="w-3 h-3 mr-1" />Email
                      </Button>
                    )}
                    <div className="text-right">
                      <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-[#A8E6CF]" style={{ width: `${cg.total_required > 0 ? (cg.completed / cg.total_required) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : tab === 'mydocs' && isCaregiver ? (
          <div className="space-y-4" data-testid="mydocs-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Upload your compliance documents (DBS, insurance, certificates). Admins will review submissions.</p>
              <Button size="sm" className="btn-primary" onClick={() => setCompUploadOpen(true)} data-testid="upload-compliance-btn">
                <Upload className="w-4 h-4 mr-1.5" />Upload Document
              </Button>
            </div>
            {myCompDocs.length === 0 ? (
              <div className="text-center py-12"><Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No compliance documents uploaded yet</p></div>
            ) : (
              <div className="space-y-2">
                {myCompDocs.map(doc => {
                  const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();
                  const isExpiringSoon = doc.expiry_date && !isExpired && (new Date(doc.expiry_date) - new Date()) / 86400000 < 30;
                  return (
                    <Card key={doc.id} className={`glass-card border-border ${isExpired ? 'border-red-500/30' : isExpiringSoon ? 'border-amber-500/30' : ''}`} data-testid={`comp-doc-${doc.id}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          doc.status === 'approved' ? 'bg-[#A8E6CF]/10' : doc.status === 'rejected' ? 'bg-red-500/10' : 'bg-amber-500/10'
                        }`}>
                          {doc.status === 'approved' ? <CheckCircle2 className="w-5 h-5 text-[#A8E6CF]" /> :
                           doc.status === 'rejected' ? <X className="w-5 h-5 text-red-400" /> :
                           <Clock className="w-5 h-5 text-amber-400" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground font-medium truncate">{doc.title || doc.filename}</p>
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
                            <span className="px-1.5 py-0.5 rounded-full bg-muted">{doc.document_type}</span>
                            <span className={`px-1.5 py-0.5 rounded-full ${
                              doc.status === 'approved' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' :
                              doc.status === 'rejected' ? 'bg-red-500/10 text-red-400' :
                              'bg-amber-500/10 text-amber-400'
                            }`}>{doc.status === 'pending_review' ? 'Pending Review' : doc.status}</span>
                            {doc.expiry_date && (
                              <span className={`${isExpired ? 'text-red-400' : isExpiringSoon ? 'text-amber-400' : ''}`}>
                                Exp: {doc.expiry_date.slice(0, 10)}
                                {isExpired && ' (Expired)'}
                              </span>
                            )}
                          </div>
                        </div>
                        {doc.storage_path && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[#00A6A0]"
                            onClick={() => window.open(`${API_URL}/api/docmgmt/caregiver/compliance-docs/${doc.id}/download`, '_blank')}
                            data-testid={`dl-comp-${doc.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Caregiver Compliance Upload Dialog */}
            <Dialog open={compUploadOpen} onOpenChange={setCompUploadOpen}>
              <DialogContent className="bg-background border-border max-w-md">
                <DialogHeader><DialogTitle className="text-foreground">Upload Compliance Document</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Document Type *</label>
                    <select value={compDocType} onChange={e => setCompDocType(e.target.value)}
                      className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="comp-type-select">
                      {COMP_DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Title</label>
                    <Input value={compTitle} onChange={e => setCompTitle(e.target.value)} placeholder="e.g. DBS Enhanced Certificate 2026"
                      className="bg-muted border-border text-foreground" data-testid="comp-title-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">File (max 25MB)</label>
                    <Input type="file" onChange={e => setCompFile(e.target.files[0])} className="bg-muted border-border text-foreground" data-testid="comp-file-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Expiry Date (optional)</label>
                    <Input type="date" value={compExpiry} onChange={e => setCompExpiry(e.target.value)}
                      className="bg-muted border-border text-foreground" data-testid="comp-expiry-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Notes</label>
                    <Input value={compNotes} onChange={e => setCompNotes(e.target.value)} placeholder="Optional notes"
                      className="bg-muted border-border text-foreground" />
                  </div>
                  <Button className="w-full btn-primary" onClick={handleComplianceUpload} disabled={compUploading} data-testid="submit-comp-upload-btn">
                    {compUploading ? 'Uploading...' : 'Upload Document'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        ) : null}
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Folder</label>
              <select value={folderType} onChange={e => setFolderType(e.target.value)} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="upload-folder-select">
                {FOLDERS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">File (max 25MB)</label>
              <Input type="file" onChange={e => setFile(e.target.files[0])} className="bg-muted border-border text-foreground" data-testid="file-upload-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Description</label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" className="bg-muted border-border text-foreground" data-testid="upload-desc-input" />
            </div>
            {isAdmin && (
              <>
                <div className="flex items-center justify-between p-3 bg-muted rounded-xl border border-border">
                  <div>
                    <p className="text-sm text-foreground font-medium">Requires Sign-Off</p>
                    <p className="text-[10px] text-muted-foreground">Training document with read confirmation + digital signature</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-testid="signoff-toggle">
                    <input type="checkbox" checked={requiresSignoff} onChange={e => setRequiresSignoff(e.target.checked)} className="sr-only peer" />
                    <div className="w-10 h-5 bg-muted peer-checked:bg-[#00A6A0] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
                  </label>
                </div>
                {requiresSignoff && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Grace Period (days)</label>
                      <Input type="number" min="1" max="90" value={gracePeriod} onChange={e => setGracePeriod(Number(e.target.value))}
                        className="bg-muted border-border text-foreground" data-testid="grace-period-input" />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Target Role</label>
                      <select value={targetRoles} onChange={e => setTargetRoles(e.target.value)} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-xs" data-testid="target-role-select">
                        <option value="caregiver">Caregivers</option>
                        <option value="client">Clients</option>
                        <option value="caregiver,client">All Staff</option>
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
            <Button className="w-full btn-primary" onClick={handleUpload} disabled={uploading} data-testid="confirm-upload-btn">
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={!!versionDialog} onOpenChange={(open) => !open && setVersionDialog(null)}>
        <DialogContent className="bg-background border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-foreground">Version History</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Existing versions */}
            {versions.length > 0 ? (
              <div className="space-y-2">
                {versions.map(v => (
                  <div key={v.id} className={`p-3 rounded-lg border ${v.is_current ? 'border-[#00A6A0]/40 bg-[#00A6A0]/5' : 'border-border bg-muted'}`} data-testid={`version-${v.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-foreground font-medium">
                          v{v.version || 1} {v.is_current && <span className="text-[10px] text-[#00A6A0] ml-1">Current</span>}
                          {v.is_deleted && <span className="text-[10px] text-red-400 ml-1">Deleted</span>}
                        </p>
                        <p className="text-[10px] text-muted-foreground">{v.filename} &bull; {new Date(v.created_at).toLocaleDateString()} &bull; by {v.uploaded_by_name}</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">{v.signoff_count} sign-offs</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No version history</p>
            )}

            {/* Upload new version */}
            <div className="border-t border-border pt-4 space-y-3">
              <p className="text-sm text-foreground font-medium">Upload New Version</p>
              <p className="text-[10px] text-muted-foreground">The current version will be archived. All previous sign-off records are preserved.</p>
              <Input type="file" onChange={e => setVersionFile(e.target.files[0])} className="bg-muted border-border text-foreground" data-testid="version-file-input" />
              <Input value={versionDesc} onChange={e => setVersionDesc(e.target.value)} placeholder="What changed in this version?" className="bg-muted border-border text-foreground text-sm" data-testid="version-desc-input" />
              <div>
                <label className="text-[10px] text-muted-foreground block mb-1">New Grace Period (days)</label>
                <Input type="number" min="1" max="90" value={versionGrace} onChange={e => setVersionGrace(Number(e.target.value))}
                  className="bg-muted border-border text-foreground w-24" data-testid="version-grace-input" />
              </div>
              <Button className="w-full btn-primary" onClick={uploadNewVersion} disabled={uploadingVersion} data-testid="upload-version-btn">
                {uploadingVersion ? 'Uploading...' : 'Upload New Version'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent className="bg-background border-border max-w-sm">
          <DialogHeader><DialogTitle className="text-foreground">Bulk Assign Training Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Select a training document to assign to all caregivers in your organization.</p>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Training Document</label>
              <select value={bulkDocId} onChange={e => setBulkDocId(e.target.value)}
                className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="bulk-doc-select">
                <option value="">Select document...</option>
                {documents.filter(d => d.requires_signoff).map(d => (
                  <option key={d.id} value={d.id}>{d.filename}</option>
                ))}
              </select>
            </div>
            <Button className="w-full btn-primary" onClick={handleBulkAssign} disabled={bulkAssigning || !bulkDocId} data-testid="confirm-bulk-assign-btn">
              {bulkAssigning ? 'Assigning...' : 'Assign to All Caregivers'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
